// Auth Service types
// These types align with the Auth Service API

import { UserRole } from './database';

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    role: UserRole;
    companyId: string | null;
    permissions: string[];
    firstName: string | null;
    lastName: string | null;
  };
}

export interface SignUpResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    role: UserRole;
    companyId: string | null;
    permissions: string[];
    firstName: string | null;
    lastName: string | null;
  };
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: UserRole;
  company_id: string | null;
  permissions: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface UpdateProfilePayload {
  first_name?: string;
  last_name?: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface UserSession {
  id: string;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
  last_used_at: string;
  expires_at: string;
  is_current: boolean;
}

export interface ActivityLog {
  id: string;
  event_type: 'login' | 'logout' | 'password_change' | 'profile_update' | 'permission_change';
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface UserInvitation {
  id: string;
  email: string;
  role: UserRole;
  company_id: string;
  invited_by: string;
  invitation_token: string;
  accepted_at: string | null;
  expires_at: string;
  created_at: string;
}

// Token payload structure
export interface AccessTokenPayload {
  sub: string; // user ID
  email: string;
  role: UserRole;
  companyId: string | null;
  permissions: string[];
  jti: string; // JWT ID (session ID)
  iat: number;
  exp: number;
  iss: string;
  aud: string;
}

// Permission constants matching backend
export const Permissions = {
  // Emission permissions
  EMISSIONS_READ: 'emissions:read',
  EMISSIONS_CREATE: 'emissions:create',
  EMISSIONS_UPDATE_OWN: 'emissions:update:own',
  EMISSIONS_UPDATE_ALL: 'emissions:update:all',
  EMISSIONS_DELETE_OWN: 'emissions:delete:own',
  EMISSIONS_DELETE_ALL: 'emissions:delete:all',

  // Company permissions
  COMPANY_READ: 'company:read',
  COMPANY_UPDATE: 'company:update',

  // Facility permissions
  FACILITIES_READ: 'facilities:read',
  FACILITIES_CREATE: 'facilities:create',
  FACILITIES_UPDATE: 'facilities:update',
  FACILITIES_DELETE: 'facilities:delete',

  // Report permissions
  REPORTS_READ: 'reports:read',
  REPORTS_GENERATE: 'reports:generate',
  REPORTS_PUBLISH: 'reports:publish',
  REPORTS_DELETE: 'reports:delete',

  // Compliance permissions
  COMPLIANCE_READ: 'compliance:read',
  COMPLIANCE_CREATE: 'compliance:create',
  COMPLIANCE_DELETE: 'compliance:delete',

  // Golden-set labelling (internal tooling — see docs/business/data-flywheel.md F1).
  // Deliberately permissions, not a role: `user_role` is a Postgres enum and
  // ALTER TYPE ... ADD VALUE cannot be rolled back, which is permanent schema debt
  // for a single user. An `esg_expert` role gets these two when external experts arrive.
  GOLDEN_READ: 'golden:read',
  GOLDEN_WRITE: 'golden:write',

  // ESG permissions
  ESG_READ: 'esg:read',
  ESG_CREATE: 'esg:create',
  ESG_UPDATE_OWN: 'esg:update:own',
  ESG_UPDATE_ALL: 'esg:update:all',
  ESG_DELETE_OWN: 'esg:delete:own',
  ESG_DELETE_ALL: 'esg:delete:all',

  // Water permissions
  WATER_READ: 'water:read',
  WATER_CREATE: 'water:create',
  WATER_UPDATE_OWN: 'water:update:own',
  WATER_UPDATE_ALL: 'water:update:all',
  WATER_DELETE_OWN: 'water:delete:own',
  WATER_DELETE_ALL: 'water:delete:all',

  // User management permissions
  USERS_READ: 'users:read',
  USERS_INVITE: 'users:invite',
  USERS_UPDATE_ROLE: 'users:update_role',
  USERS_DEACTIVATE: 'users:deactivate',
} as const;

export type Permission = typeof Permissions[keyof typeof Permissions];
